import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/integrations/dms`;
const FILES = ["GST_Ro_Billing.xlsx", "RO_List.xlsx", "Warranty.xlsx", "Sales.xlsx"];

export function copyText(text, label) {
  navigator.clipboard?.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => toast.error("Copy failed — select and copy manually"));
}

const simpleExample = (key) => `# One call — the server stores the files and processes them in the background.
# Answer within ~20 s: 200 + full result (small batch)  |  202 + job_id (big batch, still loading)
curl -sS -H "Authorization: Bearer ${key}" \\
  ${FILES.map((f) => `-F "files=@${f}"`).join(" ")} \\
  -F "as_of=$(date +%F)" ${BASE}/upload

# Check a job (batch_id in the 200 answer = job_id): status uploading → queued → running → done | failed
curl -sS -H "Authorization: Bearer ${key}" ${BASE}/jobs/<job_id>
# Last 20 jobs pushed with this key
curl -sS -H "Authorization: Bearer ${key}" ${BASE}/jobs
# Add -F "background=true" to get 202 + job_id immediately without waiting`;

const chunkedExample = (key) => `#!/usr/bin/env bash
# Chunked push (4 MB parts) — use on slow links where sending one big request takes over a minute. Needs curl + split.
set -euo pipefail
KEY="${key}"
API="${BASE}"
AS_OF="\${1:-$(date +%F)}"
FILES=(${FILES.join(" ")})
CHUNK=$((4*1024*1024))
spec=""
for f in "\${FILES[@]}"; do
  size=$(stat -c%s "$f"); chunks=$(( (size + CHUNK - 1) / CHUNK ))
  spec+="{\\"name\\":\\"$(basename "$f")\\",\\"size\\":$size,\\"chunks\\":$chunks},"
done
JOB=$(curl -sS -f -H "Authorization: Bearer $KEY" -H "Content-Type: application/json" \\
  -d "{\\"as_of\\":\\"$AS_OF\\",\\"files\\":[\${spec%,}]}" "$API/jobs" | sed -n 's/.*"job_id":"\\([^"]*\\)".*/\\1/p')
i=0
for f in "\${FILES[@]}"; do
  tmp=$(mktemp -d); split -b $CHUNK -d -a 4 "$f" "$tmp/part."; n=0
  for p in "$tmp"/part.*; do
    curl -sS -f -X PUT -H "Authorization: Bearer $KEY" -H "Content-Type: application/octet-stream" \\
      --data-binary "@$p" "$API/jobs/$JOB/files/$i/chunks/$n" > /dev/null
    n=$((n+1))
  done
  rm -rf "$tmp"; i=$((i+1))
done
curl -sS -f -X POST -H "Authorization: Bearer $KEY" "$API/jobs/$JOB/start" > /dev/null
until curl -sS -H "Authorization: Bearer $KEY" "$API/jobs/$JOB" | grep -Eq '"status":"(done|failed)"'; do sleep 3; done
curl -sS -H "Authorization: Bearer $KEY" "$API/jobs/$JOB"`;

function Snippet({ title, text, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <details className="text-xs" open={open} onToggle={(e) => setOpen(e.target.open)}>
      <summary className="cursor-pointer text-muted-foreground hover:text-foreground" data-testid={`${testId}-toggle`}>{title}</summary>
      <div className="mt-1 flex items-start gap-2">
        <pre className="flex-1 overflow-auto rounded bg-white border p-2 text-[11px] font-mono whitespace-pre max-h-72" data-testid={testId}>{text}</pre>
        <Button size="sm" variant="ghost" onClick={() => copyText(text, title)} data-testid={`${testId}-copy`}><Copy size={14} /></Button>
      </div>
    </details>
  );
}

export function ApiGuide({ apiKey = "<API_KEY>", idPrefix = "svc-apikey" }) {
  return (
    <div className="space-y-1.5" data-testid={`${idPrefix}-guide`}>
      <Snippet title="Example 1 · push the 4 daily DMS files in one call (recommended)" text={simpleExample(apiKey)} testId={`${idPrefix}-curl`} />
      <Snippet title="Example 2 · chunked push script for slow links (bash, 4 MB parts)" text={chunkedExample(apiKey)} testId={`${idPrefix}-chunked`} />
      <p className="text-[11px] text-muted-foreground">Both paths load the data identically and never time out at the proxy: the files are stored first, then processed in the background. A 202 answer means "received, still loading" — the data lands even if the job does not poll.</p>
    </div>
  );
}
